'use client'

import { useEffect, useMemo, useState } from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';

function normalizeCategory(value) {
  return String(value || '').trim();
}

export default function ArchiveGallery({ items, year, categories = [] }) {
  const [activeCategory, setActiveCategory] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeModal, setActiveModal] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const visibleCategories = useMemo(() => {
    const categoryMap = new Map();

    items.forEach((item) => {
      const categoryName = normalizeCategory(item.category);

      if (!categoryName) {
        return;
      }

      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, []);
      }

      categoryMap.get(categoryName).push(item);
    });

    return categories
      .map((category) => ({
        ...category,
        name: normalizeCategory(category.name),
      }))
      .filter((category) => category.name && categoryMap.has(category.name))
      .map((category) => ({
        ...category,
        items: categoryMap.get(category.name),
      }));
  }, [categories, items]);

  useEffect(() => {
    if (!visibleCategories.length) {
      setActiveCategory('');
      return;
    }

    const hasActiveCategory = visibleCategories.some((category) => category.name === activeCategory);

    if (!hasActiveCategory) {
      setActiveCategory(visibleCategories[0].name);
    }
  }, [activeCategory, visibleCategories]);

  const filteredItems = useMemo(() => {
    if (!activeCategory) {
      return [];
    }

    return items.filter((item) => normalizeCategory(item.category) === activeCategory);
  }, [activeCategory, items]);

  useEffect(() => {
    if (!filteredItems.length) {
      setActiveModal(false);
      setCurrentImageIndex(0);
      return;
    }

    if (currentImageIndex >= filteredItems.length) {
      setCurrentImageIndex(0);
    }
  }, [currentImageIndex, filteredItems]);

  useEffect(() => {
    if (!activeModal) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setActiveModal(false);
        return;
      }

      if (event.key === 'ArrowLeft') {
        handleMovePrev();
        return;
      }

      if (event.key === 'ArrowRight') {
        handleMoveNext();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeModal, filteredItems.length]);

  if (!items?.length || !visibleCategories.length) {
    return <p className="text-muted mb-0">Galeria foto va fi disponibila in curand.</p>;
  }

  function handleOpen(index) {
    setCurrentImageIndex(index);
    setActiveModal(true);
  }

  function handleMovePrev() {
    setCurrentImageIndex((prevIndex) => (prevIndex + filteredItems.length - 1) % filteredItems.length);
  }

  function handleMoveNext() {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % filteredItems.length);
  }

  const masonryItems = filteredItems.map((item, index) => (
    <div className="spacing" key={`${item.id ?? item.imageUrl}-${activeCategory}-${index}`}>
      <button
        type="button"
        className="card portfolio portfolio-modern portfolio-primary rounded overflow-hidden w-100 border-0 shadow-sm p-0 bg-white text-start"
        onClick={() => handleOpen(index)}
      >
        <div className="card-img position-relative overflow-hidden bg-light">
          <img
            src={item.imageUrl}
            alt={`Galerie Miss Plaja ${year} ${activeCategory} fotografia ${index + 1}`}
            loading="lazy"
            decoding="async"
            className="img-fluid w-100"
            style={{ display: 'block', height: 'auto' }}
          />
        </div>
      </button>
    </div>
  ));

  return (
    <>
      <div className="row justify-content-center mb-4">
        <div className="col-12 text-center">
          <ul className="list-unstyled d-flex flex-wrap justify-content-center gap-3 mb-0">
            {visibleCategories.map((category) => {
              const isActive = category.name === activeCategory;

              return (
                <li className="mb-0" key={category.id ?? category.name}>
                  <button
                    type="button"
                    className={`btn rounded-pill px-4 py-2 fw-semibold ${
                      isActive ? 'btn-primary shadow-sm' : 'btn-light border text-muted'
                    }`}
                    onClick={() => setActiveCategory(category.name)}
                    aria-pressed={isActive}
                  >
                    {category.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-12">
          {mounted ? (
            <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 576: 2, 767: 3, 991: 4 }}>
              <Masonry>{masonryItems}</Masonry>
            </ResponsiveMasonry>
          ) : (
            <div className="row g-3">
              {filteredItems.map((item, index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={`${item.id ?? item.imageUrl}-${index}`}>
                  <button
                    type="button"
                    className="card portfolio portfolio-modern portfolio-primary rounded overflow-hidden w-100 border-0 shadow-sm p-0 bg-white text-start"
                    onClick={() => handleOpen(index)}
                  >
                    <div className="card-img position-relative overflow-hidden bg-light">
                      <img
                        src={item.imageUrl}
                        alt={`Galerie Miss Plaja ${year} ${activeCategory} fotografia ${index + 1}`}
                        loading="lazy"
                        decoding="async"
                        className="img-fluid w-100"
                        style={{ display: 'block', height: 'auto' }}
                      />
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {activeModal && filteredItems[currentImageIndex] ? (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.82)' }}
          role="dialog"
          aria-modal="true"
          onClick={() => setActiveModal(false)}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg overflow-hidden">
              <div className="modal-header">
                <h5 className="modal-title">
                  {activeCategory} · {currentImageIndex + 1} / {filteredItems.length}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setActiveModal(false)}
                />
              </div>
              <div className="modal-body p-0 bg-dark position-relative">
                {filteredItems.length > 1 ? (
                  <button
                    type="button"
                    className="btn btn-light position-absolute top-50 start-0 translate-middle-y ms-3"
                    onClick={handleMovePrev}
                    aria-label="Imaginea anterioara"
                  >
                    {'<'}
                  </button>
                ) : null}

                <img
                  src={filteredItems[currentImageIndex].imageUrl}
                  alt={`Galerie Miss Plaja ${year} ${activeCategory} fotografia ${currentImageIndex + 1}`}
                  className="w-100"
                  style={{ maxHeight: '85vh', objectFit: 'contain', display: 'block' }}
                />

                {filteredItems.length > 1 ? (
                  <button
                    type="button"
                    className="btn btn-light position-absolute top-50 end-0 translate-middle-y me-3"
                    onClick={handleMoveNext}
                    aria-label="Imaginea urmatoare"
                  >
                    {'>'}
                  </button>
                ) : null}
              </div>
              <div className="modal-footer justify-content-between">
                <span className="text-muted small">
                  Categoria: {activeCategory}
                </span>
                <span className="text-muted small">
                  Foloseste tastele stanga/dreapta pentru navigare.
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
